/* eslint-disable max-lines */
/* eslint-disable no-param-reassign */
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import idleCallback from "common/utils/ui/idle_callback";
import initDeliveryDateSimple from "components/jiffy/product-details-delivery-date-simple/product-details-delivery-date-simple";
import { calculateTotalQuantity, buildSizesDisplay, buildDtfData, buildMadeParams } from "./utils";
import { updateHeaderCounter } from "../masthead-cart/masthead-cart";

const storeConfig = {
  namespaced: true,

  state: () => ({
    variantsData: [],
    productData: {},
    experiments: {},
    currentLineItem: null,
    currentFormData: null,
    productsSubtotal: 0,
    currentTotalPrice: 0
  }),

  getters: {
    variantsData: state => state.variantsData,
    productData: state => state.productData,
    experiments: state => state.experiments,
    currentLineItem: state => state.currentLineItem,
    currentFormData: state => state.currentFormData,
    productsSubtotal: state => state.productsSubtotal,
    currentTotalPrice: state => state.currentTotalPrice
  },

  mutations: {
    setVariantsData(state, variants) {
      state.variantsData = variants;
    },

    setProductData(state, product) {
      state.productData = product;
    },

    setCurrentLineItem(state, lineItem) {
      state.currentLineItem = lineItem;
    },

    setCurrentFormData(state, formData) {
      state.currentFormData = formData;
    },

    clearCurrentOperation(state) {
      state.currentLineItem = null;
      state.currentFormData = null;
    },

    setProductsSubtotal(state, subtotal) {
      state.productsSubtotal = subtotal;
    },

    setCurrentTotalPrice(state, totalPrice) {
      state.currentTotalPrice = totalPrice;
    }
  },

  actions: {
    async fetchVariantsData({ commit }, lineItem) {
      commit("setCurrentLineItem", lineItem);

      return new Promise((resolve, reject) => {
        api.vueCart
          .getCartVariants({
            id: lineItem.compositionId,
            count: lineItem.printSizes?.length || 1
          })
          .then(({ success, variants }) => {
            if (!success) {
              reject(new Error("Error fetching variants data. Please try again."));
              return;
            }
            commit("setVariantsData", variants);
            resolve(variants);
          })
          .catch(_error => {
            reject(new Error("Error fetching variants data. Please try again."));
          });
      });
    },

    fetchProductData({ commit }, lineItem) {
      commit("setCurrentLineItem", lineItem);

      return new Promise((resolve, reject) => {
        api.printOnDemand
          .getProduct({
            catalog_number: lineItem.productStyle
          })
          .then(response => {
            commit("setProductData", objNamesToCamelCase(response));
            resolve(response);
          })
          .catch(_error => {
            reject(new Error("Error fetching product data. Please try again."));
          });
      });
    },

    async addToCart({ commit, dispatch }, formData) {
      commit("setCurrentFormData", formData);

      return new Promise((resolve, reject) => {
        api.cart
          .madeReorder(formData)
          .then(async response => {
            await dispatch("showContinueShoppingModal", response);
            await initDeliveryDateSimple(true);
            updateHeaderCounter(response.total_items_qty);
            commit("setProductsSubtotal", response.products_subtotal);

            commit("clearCurrentOperation");

            resolve(response);
          })
          .catch(e => {
            reject(e);
            window.Honeybadger?.notify("Failed to reorder item from Orders/Jiffy Hub", {
              params: {
                error: JSON.stringify(e),
                formData: JSON.stringify(formData)
              }
            });
          });
      });
    },

    showContinueShoppingModal({ state, getters }, cartResponse) {
      const { currentLineItem: lineItem, currentFormData: formData } = state;

      if (!lineItem || !formData) return;

      const { variants, placements } = formData;
      const { variantsData } = getters;

      // Prepare event data
      const eventData = {
        dtfData: buildDtfData({
          lineItem,
          totalPrice: state.currentTotalPrice,
          totalQuantity: calculateTotalQuantity(variants),
          sizesDisplay: buildSizesDisplay(variants, variantsData)
        }),
        madeParams: buildMadeParams(placements, lineItem),
        cartResponse: {
          ...cartResponse,
          pre_cut: cartResponse.pre_cut || false,
          replace_images: cartResponse.replace_images || false,
          artwork_urls: cartResponse.artwork_urls || [],
          notices: cartResponse.notices || {},
          is_made: true
        }
      };

      idleCallback(() => {
        const $upsellContainer = document.querySelector(".js-continue-shopping-services-preview");
        if ($upsellContainer) {
          $upsellContainer.classList.add("hidden");
        }

        EventBus.publish(Events.MOBILE_GRID_SUBMIT, {
          dtfData: eventData.dtfData,
          madeParams: eventData.madeParams
        });

        EventBus.publish(Events.ITEMS_ADDED_TO_CART, eventData.cartResponse);
      });
    }
  }
};

const createStoreModule = () => storeConfig;

export default createStoreModule;
